import { initializeApp, type FirebaseOptions } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";

let messagingInitialized = false;

function getFirebaseConfig(): FirebaseOptions | null {
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
  const authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  const storageBucket = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET;
  const messagingSenderId = process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID;
  const appId = process.env.NEXT_PUBLIC_FIREBASE_APP_ID;

  if (!apiKey || !authDomain || !projectId || !storageBucket || !messagingSenderId || !appId) {
    return null;
  }

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  };
}

/**
 * Attempts to fetch a Web Push token via Firebase Messaging.
 * Returns null when unsupported, missing config, denied permission, or on errors.
 */
export async function getWebPushToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const config = getFirebaseConfig();
  if (!config) return null;

  const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

  try {
    if (!(await isSupported())) return null;

    if (!messagingInitialized) {
      initializeApp(config);
      messagingInitialized = true;
    }

    const messaging = getMessaging();

    // Only request permission if not already granted; avoid repeated prompts.
    if (Notification.permission === "denied") return null;
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return null;
    }

    const token = await getToken(messaging, { vapidKey });
    return token || null;
  } catch (err) {
    console.warn("Web push token retrieval failed", err);
    return null;
  }
}
